// AuthProvider restaura sessao no boot. Le refresh do storage; se houver,
// chama /users/me — que dispara 401 -> refresh silencioso via interceptor
// se o access estiver expirado. Se /users/me sucede, popula auth.store.
// Se falha (sem refresh, refresh expirado, rede), deixa store em "deslogado"
// e useProtectedRoute redireciona para /login.
//
// Em paralelo, dispara `logoutRetryQueue.drain()` para revogar sessions que
// foram logadas offline em boots anteriores. Fire-and-forget — nao bloqueia
// o estado do auth atual (drain usa ky raw, nao toca tokenManager).
//
// Espelha mobile (`grana-money-app/src/shared/auth/auth-provider.tsx`).

import { useEffect, type ReactNode } from 'react';

import { useAuthStore } from './auth.store';
import { logoutRetryQueue } from './logout-retry-queue';
import { tokenManager } from './token-manager';

import type { User } from '@/features/auth/types/auth.types';
import { api, RefreshRateLimitedError } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/client.types';
import { logger } from '@/shared/lib/logger';

// Backoff de re-tentativa quando o refresh toma 429 no boot. Mantemos
// `isLoading=true` entre tentativas (guard mostra o skeleton, NAO o /login),
// recuperando a sessao quando o limiter esfria. So deslogamos se esgotar.
const REFRESH_RETRY_DELAYS_MS = [1500, 3000, 6000];

export function AuthProvider({ children }: { children: ReactNode }) {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    void restoreSession(0);
    void logoutRetryQueue.drain().catch((err) => {
      logger.auth.warn('Logout retry queue drain threw unexpectedly:', err);
    });

    async function restoreSession(attempt: number) {
      try {
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
          // Sem refresh -> nao ha sessao para restaurar. Cair direto para
          // "deslogado" sem loading -> AuthLayout fica visivel imediatamente.
          if (!cancelled) setLoading(false);
          return;
        }

        // Chamar /users/me. Se access expirou, interceptor faz refresh
        // automatico e retenta; aqui so vemos sucesso ou falha final.
        const response = await api.get('users/me').json<ApiResponse<User>>();
        if (!cancelled) setUser(response.data);
      } catch (err) {
        if (cancelled) return;

        // 429 no refresh = transitorio (limiter). Re-tenta com backoff
        // mantendo `isLoading=true` em vez de deslogar — evita o "reload ->
        // login" sob tempestade de refetch / rate-limit.
        if (err instanceof RefreshRateLimitedError && attempt < REFRESH_RETRY_DELAYS_MS.length) {
          const delay = REFRESH_RETRY_DELAYS_MS[attempt]!;
          logger.auth.warn(
            `Refresh rate-limited (429); re-tentando em ${delay}ms (${attempt + 1}/${REFRESH_RETRY_DELAYS_MS.length})`,
          );
          retryTimer = setTimeout(() => void restoreSession(attempt + 1), delay);
          return;
        }

        logger.auth.warn('Session restore failed:', err);
        logout();
      }
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [setUser, setLoading, logout]);

  return <>{children}</>;
}
